import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useRevalidator } from 'react-router'
import { ProjectContext } from '../../contexts'
import { inferPageContext } from './pageContext'
import { ChevronDownIcon, PlusIcon, SendIcon } from '../../ui/icons'
import { chatApi } from './api'
import Markdown from './Markdown'
import './ChatBubble.css'
import {
    AffectedResource,
    ChatConversation,
    ChatMessage,
    ChatStreamEvent,
    PageContext,
} from './types'

interface StreamingState {
    text: string
}

const emptyStreaming: StreamingState = { text: '' }

const ChatIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
    </svg>
)

const MinimizeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
    </svg>
)

const MaximizeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
    </svg>
)

const RestoreIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V3.75M9 9H3.75M9 9 3.75 3.75M9 15v5.25M9 15H3.75M9 15l-5.25 5.25M15 9h5.25M15 9V3.75M15 9l5.25-5.25M15 15h5.25M15 15v5.25m0-5.25 5.25 5.25" />
    </svg>
)

const formatElapsed = (seconds: number) => `${Math.max(1, Math.round(seconds))}s`

interface MessageMeta {
    durationSec?: number
    isFinalAssistant?: boolean
}

const buildMessageMeta = (messages: ChatMessage[]): Record<number, MessageMeta> => {
    const meta: Record<number, MessageMeta> = {}
    let lastUserAt: number | null = null
    let pendingFinalId: number | null = null

    for (const m of messages) {
        if (m.role === 'user') {
            // Close out the prior turn — whoever was pending becomes the final assistant of that turn
            if (pendingFinalId !== null) {
                meta[pendingFinalId] = { ...meta[pendingFinalId], isFinalAssistant: true }
                pendingFinalId = null
            }
            lastUserAt = new Date(m.created_at).getTime()
        } else if (m.role === 'assistant') {
            const hasText = m.content.some(b => b.type === 'text' && (b as { text: string }).text)
            if (hasText) {
                pendingFinalId = m.id
                if (lastUserAt) {
                    const seconds = (new Date(m.created_at).getTime() - lastUserAt) / 1000
                    meta[m.id] = { durationSec: seconds }
                }
            }
        }
    }
    if (pendingFinalId !== null) {
        meta[pendingFinalId] = { ...meta[pendingFinalId], isFinalAssistant: true }
    }
    return meta
}

interface ChatPanelProps {
    onClose: () => void
    onToggleMaximize: () => void
    maximized: boolean
}

const resourceMatchesPage = (resource: AffectedResource, ctx: PageContext): boolean => {
    if (resource.type === 'campaign') {
        if (ctx.resource === 'campaign' && ctx.id === resource.id) return true
        if (ctx.resource === 'campaigns') return true
        return false
    }
    if (resource.type === 'template') {
        if (ctx.resource === 'campaign' && resource.campaign_id && ctx.id === resource.campaign_id) return true
        return false
    }
    if (resource.type === 'journey') {
        if (ctx.resource === 'journey' && ctx.id === resource.id) return true
        if (ctx.resource === 'journeys') return true
        return false
    }
    if (resource.type === 'list') {
        if (ctx.resource === 'list' && ctx.id === resource.id) return true
        if (ctx.resource === 'lists') return true
        return false
    }
    if (resource.type === 'folder') {
        return ctx.resource === 'campaigns' || ctx.resource === 'journeys' || ctx.resource === 'lists'
    }
    return false
}

const ChatPanel = ({ onClose, onToggleMaximize, maximized }: ChatPanelProps) => {
    const { t } = useTranslation()
    const location = useLocation()
    const revalidator = useRevalidator()
    const [project] = useContext(ProjectContext)

    const [conversations, setConversations] = useState<ChatConversation[]>([])
    const [activeId, setActiveId] = useState<number | null>(null)
    const [activeConversation, setActiveConversation] = useState<ChatConversation | null>(null)
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [composerText, setComposerText] = useState('')
    const [historyOpen, setHistoryOpen] = useState(false)
    const [sending, setSending] = useState(false)
    const [streaming, setStreaming] = useState<StreamingState>(emptyStreaming)
    const [streamStartedAt, setStreamStartedAt] = useState<number | null>(null)
    const [nowTick, setNowTick] = useState(0)
    const abortRef = useRef<AbortController | null>(null)
    const scrollRef = useRef<HTMLDivElement | null>(null)
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)

    useEffect(() => {
        const el = textareaRef.current
        if (!el) return
        el.style.height = 'auto'
        el.style.height = `${Math.min(el.scrollHeight, 140)}px`
    }, [composerText])

    const refreshConversations = useCallback(async () => {
        const result = await chatApi.conversations.list(project.id, { limit: 25 })
        setConversations(result.results)
        return result.results
    }, [project.id])

    const didAutoload = useRef(false)
    useEffect(() => {
        let cancelled = false
        void (async () => {
            const list = await refreshConversations()
            if (cancelled || didAutoload.current) return
            didAutoload.current = true
            if (list.length > 0) {
                const conv = await chatApi.conversations.get(project.id, list[0].id)
                if (cancelled) return
                setActiveId(conv.id)
                setActiveConversation(conv)
                setMessages(conv.messages ?? [])
            }
        })()
        return () => { cancelled = true }
    }, [project.id, refreshConversations])

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
    }, [messages, streaming, nowTick])

    useEffect(() => {
        if (streamStartedAt === null) return
        const interval = setInterval(() => setNowTick(Date.now()), 1000)
        return () => clearInterval(interval)
    }, [streamStartedAt])

    const loadConversation = useCallback(async (id: number) => {
        const conv = await chatApi.conversations.get(project.id, id)
        setActiveId(id)
        setActiveConversation(conv)
        setMessages(conv.messages ?? [])
        setStreaming(emptyStreaming)
    }, [project.id])

    const handleNewChat = useCallback(() => {
        setActiveId(null)
        setActiveConversation(null)
        setMessages([])
        setStreaming(emptyStreaming)
        setHistoryOpen(false)
    }, [])

    const handleSend = useCallback(async () => {
        const trimmed = composerText.trim()
        if (!trimmed || sending) return
        setSending(true)
        setStreamStartedAt(Date.now())
        setNowTick(Date.now())
        setComposerText('')

        let conversation = activeConversation
        if (!conversation) {
            conversation = await chatApi.conversations.create(project.id)
            setActiveConversation(conversation)
            setActiveId(conversation.id)
        }

        const provisionalUserId = -Date.now()
        setMessages(prev => [
            ...prev,
            {
                id: provisionalUserId,
                conversation_id: conversation!.id,
                role: 'user',
                content: [{ type: 'text', text: trimmed }],
                created_at: new Date().toISOString(),
            },
        ])
        setStreaming(emptyStreaming)

        const controller = new AbortController()
        abortRef.current = controller

        try {
            const pageContext = inferPageContext(location.pathname)
            for await (const event of chatApi.stream(project.id, conversation.id, trimmed, controller.signal, pageContext)) {
                applyStreamEvent(event)
            }
        } finally {
            abortRef.current = null
            setSending(false)
            setStreamStartedAt(null)
            await refreshConversations()
            if (conversation) {
                const fresh = await chatApi.conversations.get(project.id, conversation.id)
                setActiveConversation(fresh)
                setMessages(fresh.messages ?? [])
                setStreaming(emptyStreaming)
            }
        }
    }, [activeConversation, composerText, location.pathname, project.id, refreshConversations, sending])

    const applyStreamEvent = (event: ChatStreamEvent) => {
        if (event.type === 'text_delta') {
            setStreaming(prev => ({ ...prev, text: prev.text + event.text }))
        } else if (event.type === 'resource_updated') {
            const ctx = inferPageContext(location.pathname)
            if (resourceMatchesPage(event.resource, ctx)) {
                void revalidator.revalidate()
            }
        } else if (event.type === 'conversation_updated') {
            setActiveConversation(prev => prev ? { ...prev, title: event.title } : prev)
        } else if (event.type === 'error') {
            setMessages(prev => [
                ...prev,
                {
                    id: -Date.now(),
                    conversation_id: activeConversation?.id ?? 0,
                    role: 'assistant',
                    content: [{ type: 'text', text: t('ai_chat_error') + ': ' + event.message }],
                    created_at: new Date().toISOString(),
                },
            ])
        }
    }

    const handleComposerKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            void handleSend()
        }
    }

    const handleHistorySelect = (id: number) => {
        setHistoryOpen(false)
        void loadConversation(id)
    }

    const title = activeConversation?.title ?? t('ai_chat_new_conversation')

    const content = (
        <>
            <div className="chat-panel-header">
                <button
                    type="button"
                    className="chat-panel-header-title"
                    onClick={() => setHistoryOpen(open => !open)}
                    title={t('ai_chat_history')}
                >
                    <span className="chat-panel-header-title-text">{title}</span>
                    <ChevronDownIcon />
                </button>
                <button
                    type="button"
                    className="chat-panel-header-action"
                    onClick={handleNewChat}
                    title={t('ai_chat_new')}
                >
                    <PlusIcon />
                </button>
                <button
                    type="button"
                    className="chat-panel-header-action"
                    onClick={onToggleMaximize}
                    title={maximized ? t('ai_chat_restore') : t('ai_chat_maximize')}
                >
                    {maximized ? <RestoreIcon /> : <MaximizeIcon />}
                </button>
                <button
                    type="button"
                    className="chat-panel-header-action"
                    onClick={onClose}
                    title={t('ai_chat_minimize')}
                >
                    <MinimizeIcon />
                </button>
                {historyOpen && (
                    <div className="chat-history-dropdown" onMouseLeave={() => setHistoryOpen(false)}>
                        <div className="chat-history-section">{t('ai_chat_history')}</div>
                        {conversations.length === 0 && (
                            <div className="chat-history-empty">{t('ai_chat_history_empty')}</div>
                        )}
                        {conversations.map(c => (
                            <button
                                key={c.id}
                                type="button"
                                className={'chat-history-item' + (c.id === activeId ? ' active' : '')}
                                onClick={() => handleHistorySelect(c.id)}
                            >
                                {c.title ?? t('ai_chat_untitled')}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="chat-panel-messages" ref={scrollRef}>
                {messages.length === 0 && !streaming.text && !sending && (
                    <div className="chat-panel-empty">{t('ai_chat_empty_state')}</div>
                )}
                {(() => {
                    const meta = buildMessageMeta(messages)
                    return messages.map(message => {
                        if (message.role === 'tool') return null
                        const textBlocks = message.content.filter(b => b.type === 'text' && (b as { text: string }).text)
                        if (textBlocks.length === 0) return null
                        const m = meta[message.id]
                        const fullText = textBlocks.map(b => (b as { text: string }).text).join('\n\n')
                        return (
                            <div key={message.id} style={{ display: 'contents' }}>
                                <div className={'chat-message ' + message.role}>
                                    {message.role === 'user' ? fullText : <Markdown text={fullText} />}
                                </div>
                                {m?.isFinalAssistant && m.durationSec !== undefined && !(sending && message === messages[messages.length - 1]) && (
                                    <div className="chat-message-meta">
                                        {t('ai_chat_replied_in', { time: formatElapsed(m.durationSec) })}
                                    </div>
                                )}
                            </div>
                        )
                    })
                })()}
                {streaming.text && (
                    <div className="chat-message assistant"><Markdown text={streaming.text} /></div>
                )}
                {sending && streamStartedAt !== null && (
                    <div className="chat-message-meta thinking">
                        {t('ai_chat_thinking', { time: formatElapsed((nowTick - streamStartedAt) / 1000) })}
                    </div>
                )}
            </div>
            <div className="chat-panel-composer">
                <textarea
                    ref={textareaRef}
                    placeholder={t('ai_chat_placeholder')}
                    value={composerText}
                    onChange={e => setComposerText(e.target.value)}
                    onKeyDown={handleComposerKey}
                    rows={1}
                />
                <button
                    type="button"
                    className="chat-panel-composer-send"
                    onClick={async () => { await handleSend() }}
                    disabled={!composerText.trim() || sending}
                    title={t('ai_chat_send')}
                >
                    <SendIcon />
                </button>
            </div>
        </>
    )

    return <div className={'chat-panel' + (maximized ? ' maximized' : '')}>{content}</div>
}

export default function ChatBubble() {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [enabled, setEnabled] = useState<boolean | null>(null)
    const [open, setOpen] = useState(false)
    const [maximized, setMaximized] = useState(false)

    useEffect(() => {
        if (!project?.id) return
        let cancelled = false
        chatApi.enabled(project.id)
            .then(value => { if (!cancelled) setEnabled(value) })
            .catch(() => { if (!cancelled) setEnabled(false) })
        return () => { cancelled = true }
    }, [project?.id])

    if (!enabled) return null

    if (!open) {
        return (
            <button
                type="button"
                className="chat-bubble-button"
                onClick={() => setOpen(true)}
                title={t('ai_chat_open')}
                aria-label={t('ai_chat_open')}
            >
                <ChatIcon />
            </button>
        )
    }

    return (
        <ChatPanel
            onClose={() => { setOpen(false); setMaximized(false) }}
            onToggleMaximize={() => setMaximized(m => !m)}
            maximized={maximized}
        />
    )
}
